import { Autocomplete, TextField, AutocompleteRenderInputParams, AutocompleteChangeReason, AutocompleteInputChangeReason } from "@mui/material";
import React, { useEffect, useState, memo, useCallback, useMemo } from "react";
import { GazetteerRecordParsed, GazetteerService } from "@shared/mirage/services/GazetteerService";
import "./AddressBox.css";
import AutocompleteIcon from "./AutocompleteIcon";
import { gazetteerState } from "@mirage/store/gazetteerStore";
import { useRecoilValue } from "recoil";
import { AddressHelper } from "@shared/mirage/helpers/AddressHelper";

export interface AddressAutocompleteProps {
    gazetteer: "local" | "national";
    localGazetteerUrl?: string;
    localGazetteerToken?: string;
    nationalGazetteerUrl?: string;
    nationalGazetteerToken?: string;
    onSelect: ({ address, addressDetails, point, addressLines }: ISelectedAddressRecord) => void;
    className?: string;
    style?: React.CSSProperties;
    size?: "small" | "medium" | undefined;
    hideAutocompleteLabel?: boolean;
    gazetteerChoice?: boolean; // by default set to false as it's used in public portal
    hideGazetteerIcon?: boolean; // by default set to true as it's used in public portal
}

const AddressAutocomplete = memo((props: AddressAutocompleteProps) => {
    const { gazetteer, onSelect, style, hideAutocompleteLabel, className = "address-box-responsive", size = "medium", gazetteerChoice = false, hideGazetteerIcon = true } = props;
    const [selectedGazetteer, setSelectedGazetteer] = useState(gazetteer);
    const gazetteerSettings = useRecoilValue(gazetteerState);
    const localGazetteerToken = props.localGazetteerToken ? props.localGazetteerToken : gazetteerSettings.localGazetteerToken;
    const localGazetteerUrl = props.localGazetteerUrl ? props.localGazetteerUrl : gazetteerSettings.localGazetteerUrl;
    const nationalGazetteerToken = props.nationalGazetteerToken ? props.nationalGazetteerToken : gazetteerSettings.nationalGazetteerToken;
    const nationalGazetteerUrl = props.nationalGazetteerUrl ? props.nationalGazetteerUrl : gazetteerSettings.nationalGazetteerUrl;
    const localGazetteerService = useMemo(() => new GazetteerService(localGazetteerUrl, localGazetteerToken), [localGazetteerToken, localGazetteerUrl]);
    const nationalGazetteerService = useMemo(() => new GazetteerService(nationalGazetteerUrl, nationalGazetteerToken), [nationalGazetteerToken, nationalGazetteerUrl]);
    const selectedGazetteerService = selectedGazetteer === "local" ? localGazetteerService : nationalGazetteerService;
    const [inputValue, setInputValue] = useState("");
    const [options, setOptions] = useState<AddressBoxOption[]>([]);
    const label = hideAutocompleteLabel ? "" : "Find Address";
    const gazetteerIconOnClick = useCallback(() => {
        setSelectedGazetteer((prev) => {
            if (prev === "local") {
                return "national";
            }
            return "local";
        });
    }, []);

    useEffect(() => {
        if (inputValue.length >= 2) {
            selectedGazetteerService.getGazetteerAddresses(inputValue).then((opts) => {
                const options = opts?.map((opt) => ({ label: opt.label, id: opt.id })) || [];
                setOptions(options);
            });
        }
    }, [inputValue, selectedGazetteer, selectedGazetteerService]);

    const getAddressById = async (value: string | AddressBoxOption | null) => {
        if (typeof value !== "string" && value?.id) {
            const data = await selectedGazetteerService.getAddressById(value.id);
            if (data?.record) {
                const record: GazetteerRecordParsed = JSON.parse(data.record);
                const point = { easting: parseInt(data.point.easting), northing: parseInt(data.point.northing) };
                const addressLines = AddressHelper.getAddressLinesFromGazetteerRecord(record);
                onSelect({ address: value.label, addressDetails: record, point, addressLines });
            }
        }
    };
    return (
        <Autocomplete
            className={className}
            freeSolo // user input is not bound to provided options.
            filterOptions={(x) => x}
            blurOnSelect // input is blurred when an option is selected
            style={style}
            size={size}
            options={options}
            onBlur={onBlur}
            inputValue={inputValue}
            onInputChange={onInputChange}
            renderOption={renderOption}
            onChange={onChange}
            renderInput={renderInput}
        />
    );

    function onBlur() {
        setOptions([]);
        setInputValue("");
    }

    function onInputChange(event: React.SyntheticEvent<Element, Event>, value: string, reason: AutocompleteInputChangeReason) {
        if (reason === "reset" || reason === "clear") {
            setInputValue("");
            setOptions([]); // clear the options after select
        } else {
            setInputValue(value);
        }
    }

    async function onChange(event: React.SyntheticEvent<Element, Event>, value: string | AddressBoxOption | null, reason: AutocompleteChangeReason) {
        if (reason === "selectOption") {
            await getAddressById(value);
        }
    }

    function renderOption(props: React.HTMLAttributes<HTMLLIElement>, option: AddressBoxOption) {
        return (
            <li {...props} key={option.id}>
                {option.label}
            </li>
        );
    }

    function renderInput(params: AutocompleteRenderInputParams) {
        const startAdornment = (
            <AutocompleteIcon
                gazetteerIconOnClick={gazetteerIconOnClick}
                selectedGazetteer={selectedGazetteer}
                gazetteerChoice={gazetteerChoice}
                hideGazetteerIcon={hideGazetteerIcon}
            />
        );
        return <TextField {...params} label={label} InputProps={{ ...params.InputProps, startAdornment }} />;
    }
});

interface AddressBoxOption {
    label: string;
    id: string;
}
export interface ISelectedAddressRecord {
    address: string;
    addressDetails: GazetteerRecordParsed;
    point: { easting: number; northing: number };
    addressLines: { line1: string; line2: string; line3: string };
}

export default AddressAutocomplete;
